import { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { signupVerifyLogoBlockSx, signupVerifyLogoImgSx } from '@/features/auth/styles/signupVerifyStyles'
import { ONBOARDING_CONSENT_HEADING } from '../lib/constants'
import { ConsentDeclinedPanel } from './ConsentDeclinedPanel'
import {
  consentAcceptButtonSx,
  consentBeforeHeadingSx,
  consentBeforeSubtitleSx,
  consentBodyTextSx,
  consentCardSx,
  consentDeclineButtonSx,
  consentFooterSx,
  consentLastUpdatedSx,
  consentPageRootSx,
  consentPolicyTitleSx,
  consentScrollRegionSx,
} from '../styles/instrumentOnboardingStyles'
import { signupSharePrimaryButtonSx, useSignupShareTheme } from '../styles/signupShareTheme'

interface ConsentPolicyCardProps {
  readonly pageSubtitle: string
  readonly policyTitle: string
  readonly bodyParagraphs: readonly string[]
  readonly lastUpdated: string
  readonly acceptLabel?: string
  readonly acceptPending?: boolean
  /** When true, footer shows decline hard-stop (Figma 4950:6971) — policy body stays visible. */
  readonly declined?: boolean
  readonly onAccept: () => void
  readonly onDecline: () => void
  readonly onRetryConsent: () => void
}

export function ConsentPolicyCard({
  pageSubtitle,
  policyTitle,
  bodyParagraphs,
  lastUpdated,
  acceptLabel = 'I Accept',
  acceptPending = false,
  declined = false,
  onAccept,
  onDecline,
  onRetryConsent,
}: ConsentPolicyCardProps) {
  const signupTheme = useSignupShareTheme()
  const scrollRef = useRef<HTMLDivElement>(null)
  /** Latched true once the user reaches the bottom; stays true if they scroll back up. */
  const [hasReachedBottom, setHasReachedBottom] = useState(false)

  const evaluateScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight <= 8
    if (atEnd) {
      setHasReachedBottom(true)
    }
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = 0
    setHasReachedBottom(false)
    const frame = requestAnimationFrame(() => {
      const node = scrollRef.current
      if (!node) return
      if (node.scrollHeight <= node.clientHeight + 8) {
        setHasReachedBottom(true)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [policyTitle])

  return (
    <Box data-testid="instrument-onboarding-consent-page" sx={consentPageRootSx}>
      <Box sx={signupVerifyLogoBlockSx}>
        <Box
          component="img"
          src={brushfireLogo}
          alt="Brushfire"
          sx={signupVerifyLogoImgSx}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
        <Typography component="h1" sx={consentBeforeHeadingSx}>
          {ONBOARDING_CONSENT_HEADING}
        </Typography>
        <Typography component="p" sx={consentBeforeSubtitleSx}>
          {pageSubtitle}
        </Typography>
      </Box>

      <Box sx={consentCardSx} data-testid="instrument-onboarding-consent-card">
        <Box
          ref={scrollRef}
          onScroll={evaluateScroll}
          data-testid="instrument-onboarding-consent-scroll"
          sx={consentScrollRegionSx}
        >
          <Typography component="h2" sx={consentPolicyTitleSx}>
            {policyTitle}
          </Typography>
          {bodyParagraphs.map((paragraph, index) => (
            <Typography key={`${index}-${paragraph.slice(0, 40)}`} component="p" sx={consentBodyTextSx}>
              {paragraph}
            </Typography>
          ))}
          <Typography component="p" sx={consentLastUpdatedSx}>
            {lastUpdated}
          </Typography>
        </Box>

        <Box sx={consentFooterSx}>
          {declined ? (
            <ConsentDeclinedPanel onRetry={onRetryConsent} />
          ) : (
            <>
              <Button
                type="button"
                variant="contained"
                fullWidth
                disableElevation
                disabled={!hasReachedBottom || acceptPending}
                onClick={onAccept}
                data-testid="instrument-onboarding-consent-accept"
                sx={[consentAcceptButtonSx, signupTheme ? signupSharePrimaryButtonSx : null]}
              >
                {acceptPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  acceptLabel
                )}
              </Button>
              <Button
                type="button"
                variant="text"
                onClick={onDecline}
                data-testid="instrument-onboarding-consent-decline"
                sx={consentDeclineButtonSx}
              >
                Decline
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}
